import type { ApiContext } from "./context";
import { ApiNotAuthorized, type TransportError, ValidationError } from "./errors";
import { fail, ok, type Result } from "./result";

export interface TransportConfig {
  /** Origin plus prefix: `/api` in a browser (same origin), `http://localhost:4001/api` in Node. */
  baseUrl: string;
  printLogs?: boolean;
  fetch?: typeof fetch;
}

export type Query = Record<string, string | number | boolean | undefined>;

export interface RequestOptions {
  body?: unknown;
  query?: Query;
}

const RELATIVE_ORIGIN = "http://relative.invalid";

export class Transport {
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly cfg: TransportConfig) {
    // Bound, never `cfg.fetch ?? fetch`. Stored on an instance and called as
    // `this.fetchImpl(...)`, the browser's fetch receives the Transport as its
    // receiver and throws "Illegal invocation" before any request is sent —
    // invisible in the network tab, and Node's fetch does not check, so it
    // reaches the browser past a green e2e suite.
    this.fetchImpl = cfg.fetch ?? globalThis.fetch.bind(globalThis);
  }

  get(ctx: ApiContext, path: string, o?: RequestOptions) {
    return this.request(ctx, "GET", path, o);
  }
  post(ctx: ApiContext, path: string, o?: RequestOptions) {
    return this.request(ctx, "POST", path, o);
  }
  put(ctx: ApiContext, path: string, o?: RequestOptions) {
    return this.request(ctx, "PUT", path, o);
  }
  patch(ctx: ApiContext, path: string, o?: RequestOptions) {
    return this.request(ctx, "PATCH", path, o);
  }
  delete(ctx: ApiContext, path: string, o?: RequestOptions) {
    return this.request(ctx, "DELETE", path, o);
  }

  async request(
    ctx: ApiContext,
    method: string,
    path: string,
    o: RequestOptions = {},
  ): Promise<Result<unknown, TransportError>> {
    const url = this.buildUrl(path, o.query);

    const headers: Record<string, string> = { Accept: "application/json" };
    if (o.body !== undefined) headers["Content-Type"] = "application/json";
    if (ctx.authToken) headers.Authorization = `Bearer ${ctx.authToken}`;

    // A failed fetch (offline, DNS, refused) throws: rare, and never a business outcome.
    const res = await this.fetchImpl(url, {
      method,
      headers,
      body: o.body === undefined ? undefined : JSON.stringify(o.body),
    });

    const text = await res.text();
    const data = text.length ? safeJson(text) : undefined;

    if (this.cfg.printLogs) {
      console.log(`${method} ${url} -> ${res.status}\n${text}`);
    }

    if (res.status === 401) return fail(new ApiNotAuthorized(url));

    if (res.status === 400 && Array.isArray(data)) {
      return fail(...data.map((e) => ValidationError.fromValidationEntry(e, url)));
    }

    if (res.status >= 400 && res.status < 500 && isErrorBody(data)) {
      return fail(ValidationError.fromErrorBody(data, url));
    }

    if (res.status < 300) return ok(data);

    throw new Error(
      `Unexpected response ${res.status} for ${method} ${url}\n` +
        `Request body: ${JSON.stringify(o.body)}\nResponse body: ${text}`,
    );
  }

  /**
   * The same request, read as a Server-Sent Events stream instead of one body.
   *
   * A separate method rather than an option on `request`, because the two answer
   * different shapes: `request` resolves a `Result` once, this yields frames
   * until the connection closes. Only the scan pipeline uses it — the backend
   * content-negotiates `POST /shop/:shopId/scan/start` on `Accept`.
   *
   * **Every failure here throws.** A stream that cannot be opened is not a
   * business outcome the caller was asked to handle, and once frames have
   * started the response headers are long gone — which is why the pipeline
   * reports its own failures as a `scan.error` frame rather than an HTTP status.
   *
   * Aborting stops the reading, not the work: the backend links the scan before
   * the pipeline runs, so it still completes and persists.
   */
  async *stream(
    ctx: ApiContext,
    method: string,
    path: string,
    o: RequestOptions & { signal?: AbortSignal } = {},
  ): AsyncGenerator<SseFrame> {
    const url = this.buildUrl(path, o.query);

    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (o.body !== undefined) headers["Content-Type"] = "application/json";
    if (ctx.authToken) headers.Authorization = `Bearer ${ctx.authToken}`;

    const res = await this.fetchImpl(url, {
      method,
      headers,
      body: o.body === undefined ? undefined : JSON.stringify(o.body),
      signal: o.signal,
    });

    if (!res.ok) {
      const detail = await res.text().catch(() => res.statusText);
      throw new Error(`Stream ${method} ${url} failed with ${res.status}
${detail}`);
    }
    if (!res.body) throw new Error(`Stream ${method} ${url} returned no body`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        // A final frame with no trailing blank line would otherwise be lost.
        const chunk = done ? decoder.decode() : decoder.decode(value, { stream: true });
        // Normalised over the whole buffer rather than per chunk, so a CRLF
        // split across two reads still collapses — frames are found by scanning
        // for a blank line.
        buffer = (buffer + chunk).replace(/\r\n/g, "\n");

        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
          const frame = parseSseFrame(buffer.slice(0, boundary));
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf("\n\n");
          if (frame) yield frame;
        }

        if (done) {
          const frame = parseSseFrame(buffer);
          if (frame) yield frame;
          return;
        }
      }
    } finally {
      reader.cancel().catch(() => {});
    }
  }

  private buildUrl(path: string, query?: Query): string {
    const base = this.cfg.baseUrl.replace(/\/?$/, "/");
    const rel = path.replace(/^\//, "");
    const absolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(base);
    const url = absolute ? new URL(rel, base) : new URL(base + rel, RELATIVE_ORIGIN);
    for (const [k, v] of Object.entries(query ?? {})) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
    return absolute ? url.toString() : url.pathname + url.search;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isErrorBody(data: unknown): data is { error: string } & Record<string, unknown> {
  return typeof data === "object" && data !== null && typeof (data as { error?: unknown }).error === "string";
}

/** One decoded SSE frame: the event name and its already-parsed JSON payload. */
export interface SseFrame {
  event: string;
  data: unknown;
}

/** Reads one SSE field: `name: value`, with at most one space after the colon. */
function sseField(line: string, name: string): string | null {
  if (!line.startsWith(`${name}:`)) return null;
  const value = line.slice(name.length + 1);
  return value.startsWith(" ") ? value.slice(1) : value;
}

/**
 * Decodes one frame.
 *
 * Returns null for anything unusable — a comment frame (`: keep-alive`), a
 * frame missing its name or its data, or malformed JSON. None of those is worth
 * ending a stream over, so the caller simply sees no frame for it.
 */
function parseSseFrame(frame: string): SseFrame | null {
  let event: string | null = null;
  const data: string[] = [];

  for (const line of frame.split("\n")) {
    event = sseField(line, "event") ?? event;
    const chunk = sseField(line, "data");
    if (chunk !== null) data.push(chunk);
  }

  if (!event || data.length === 0) return null;

  try {
    // Multi-line data fields join with a newline, per the SSE spec.
    return { event, data: JSON.parse(data.join("\n")) };
  } catch {
    return null;
  }
}
